import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { elisaForCompaniesMsg, t } from '../i18n/index.js';
import { isInBrowser } from '../utils/ssrUtils.js';
import {
  INDEX_FOLLOW,
  ROBOTS_META_SELECTOR,
  createCanonicalUrl,
  setElementBySelectorAndContent,
  setElementBySelectorAndHref,
} from './useMeta.js';

type IdeatMeta = {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  pagination?: PaginationMeta;
};

type PaginationMeta = {
  currentPage: number;
  totalPages: number;
  baseUrl: string;
};

const getFullTitle = (title: string): string => {
  const msg = t.V0XR(elisaForCompaniesMsg);
  return title.includes(msg) ? title : `${title} | ${msg}`;
};

const setOrRemoveLinkRel = (rel: string, href: string | undefined) => {
  if (!isInBrowser()) {
    return;
  }
  const selector = `link[rel="${rel}"]`;
  const existing = document.querySelector(selector);
  if (href) {
    if (existing) {
      (existing as HTMLLinkElement).href = href;
    } else {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
    }
  } else {
    existing?.remove();
  }
};

function updatePaginationLinks(pagination: PaginationMeta | undefined) {
  if (!pagination) {
    setOrRemoveLinkRel('prev', undefined);
    setOrRemoveLinkRel('next', undefined);
    return;
  }
  const { currentPage, totalPages, baseUrl } = pagination;
  let prevUrl: string | undefined;
  if (currentPage > 1) {
    const prevPath = currentPage === 2 ? baseUrl : `${baseUrl}/page/${currentPage - 1}`;
    prevUrl = createCanonicalUrl(prevPath);
  }

  let nextUrl: string | undefined;
  if (currentPage < totalPages) {
    nextUrl = createCanonicalUrl(`${baseUrl}/page/${currentPage + 1}`);
  }

  setOrRemoveLinkRel('prev', prevUrl);
  setOrRemoveLinkRel('next', nextUrl);
}

function updateGeneralMeta(meta: IdeatMeta) {
  const fullTitle = getFullTitle(meta.title);
  if (isInBrowser()) {
    document.title = fullTitle;
  }
  setElementBySelectorAndContent('[name="description"]', meta.description);
  setElementBySelectorAndContent('[property="og:title"]', fullTitle);
  setElementBySelectorAndContent('[property="og:description"]', meta.description);
  if (meta.image) {
    setElementBySelectorAndContent('[property="og:image"]', meta.image);
  }
  if (meta.imageAlt) {
    setElementBySelectorAndContent('[property="og:image:alt"]', meta.imageAlt);
  }
}

export const useIdeatMeta = (meta: IdeatMeta | undefined) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const canonicalUrl = createCanonicalUrl(pathname);
    setElementBySelectorAndHref('[rel="canonical"]', canonicalUrl);
    setElementBySelectorAndContent('[property="og:url"]', canonicalUrl);
    setElementBySelectorAndContent(ROBOTS_META_SELECTOR, INDEX_FOLLOW);
    if (meta) {
      updateGeneralMeta(meta);
    }
    updatePaginationLinks(meta?.pagination);

    return () => {
      setOrRemoveLinkRel('prev', undefined);
      setOrRemoveLinkRel('next', undefined);
    };
  }, [meta, pathname]);
};
